<template>
  <div class="endpoint_head">
    <h2>Update staff</h2>

    <div class="route">
      <i
        @click="
          $router.push('/documentation/api/staff#update');
          $toast.info('Set URL to endpoint');
        "
        :style="{ color: 'var(--clr-secondary)', cursor: 'pointer' }"
        class="fas fa-hashtag"
      ></i>
      <p class="tag tag-blue">POST</p>
      <p class="url">
        {{ apiUrl }}<span>/salons/:salonId/staff/:staffId</span>
      </p>
    </div>

    <div class="description">
      <p>
        Updates a staff member on a salon, as well as their Raserva account if
        applicable
      </p>
    </div>
  </div>

  <div class="endpoint_body">
    <div class="request">
      <h3>Request</h3>

      <!-- Headers -->
      <div class="endpoint_body_section path">
        <div class="head">
          <h4>Headers</h4>
        </div>

        <ul>
          <li>
            <div class="col value">
              <p class="bold">Authorization</p>
              <p class="examples">Examples: <span>Bearer {Token}</span></p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>
        </ul>
      </div>

      <!-- Path params -->
      <div class="endpoint_body_section path">
        <div class="head">
          <h4>Path Parameters</h4>
        </div>

        <ul>
          <li>
            <div class="col value">
              <p class="bold">salonId</p>
              <p class="description">ID of salon to add staff to</p>
              <p class="examples">
                Examples: <span>5f8a1d9b-3b0b-4c9a-9c0d-1a2b3c4d5e6f</span>
              </p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <li>
            <div class="col value">
              <p class="bold">staffId</p>
              <p class="description">
                ID of staff to update (Note this is the "_id" and NOT the
                "staffId")
              </p>
              <p class="examples">
                Examples: <span>5f8a1d9b-3b0b-4c9a-9c0d-1a2b3c4d5e6d</span>
              </p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>
        </ul>
      </div>

      <!-- Body -->
      <div class="endpoint_body_section path">
        <div class="head">
          <h4>Body</h4>
          <p>application/json</p>
        </div>

        <ul>
          <!-- First Name -->
          <li>
            <div class="col value">
              <p class="bold">firstName</p>
              <p class="description">String</p>
              <p class="examples">Examples: <span>John</span></p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <!-- Last Name -->
          <li>
            <div class="col value">
              <p class="bold">lastName</p>
              <p class="description">String</p>
              <p class="examples">Examples: <span>Doe</span></p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <!-- Title -->
          <li>
            <div class="col value">
              <p class="bold">title</p>
              <p class="description">String</p>
              <p class="examples">Examples: <span>Manager</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Email -->
          <li>
            <div class="col value">
              <p class="bold">email</p>
              <p class="description">
                String - Must be unique, no other Raserva staff account with
                this email can exist. If the account already exist, use the
                "invite" feature to invite the staff account to the salon.
              </p>
              <p class="examples">Examples: <span>john@doe.com</span></p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <!-- Account code -->
          <li>
            <div class="col value">
              <p class="bold">accountCode</p>
              <p class="description">
                String - Used to clock in, clock out, and for other permission
                based features - Must be unique per salon (No other team member
                can have the same account code)
              </p>
              <p class="examples">Examples: <span>1234</span></p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <!-- Phone number -->
          <li>
            <div class="col value">
              <p class="bold">phoneNumber</p>
              <p class="description">String</p>
              <p class="examples">Examples: <span>2175551234</span></p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <!-- Birthday -->
          <li>
            <div class="col value">
              <p class="bold">birthday</p>
              <p class="description">Javascript date object</p>
              <!-- <p class="examples">Examples: <span>2175551234</span></p> -->
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Profile Image -->
          <li>
            <div class="col value">
              <p class="bold">profileImage</p>
              <p class="description">String - URL to image</p>
              <p class="examples">
                Examples:
                <span>https://www.images.google.com/ryan-reynolds</span>
              </p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- On Booking -->
          <li>
            <div class="col value">
              <p class="bold">onBooking</p>
              <p class="description">
                Boolean - Determines if the staff member is available for online
                booking
              </p>
              <p class="examples">Examples: <span>true</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- On Calendar -->
          <li>
            <div class="col value">
              <p class="bold">onCalendar</p>
              <p class="description">
                Boolean - Determines if the staff member is available for
                calendar bookings
              </p>
              <p class="examples">Examples: <span>true</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Commission -->
          <li>
            <div class="col value">
              <p class="bold">commission</p>
              <p class="description">
                Number - Percentage of commission the staff member receives
              </p>
              <p class="examples">Examples: <span>60</span></p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <!-- Track Hourly -->
          <li>
            <div class="col value">
              <p class="bold">trackHourly</p>
              <p class="description">
                Boolean - Determines if the system should record the staff
                members clocked-in hours
              </p>
              <p class="examples">Examples: <span>false</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Hourly Rate -->
          <li>
            <div class="col value">
              <p class="bold">hourlyRate</p>
              <p class="description">
                Number - Amount per hour ($) the staff member is paid
              </p>
              <p class="examples">Examples: <span>15</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Split - Check -->
          <li>
            <div class="col value">
              <p class="bold">splitCheck</p>
              <p class="description">
                Number - Percentage of pay out the staff member receives via
                check (This plus splitOther must equal 100%) [Default = 100]
              </p>
              <p class="examples">Examples: <span>70</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Split - Other -->
          <li>
            <div class="col value">
              <p class="bold">splitOther</p>
              <p class="description">
                Number - Percentage of pay out the staff member receives via
                other (This plus splitCheck must equal 100%) [Default = 0]
              </p>
              <p class="examples">Examples: <span>30</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Daily Fee -->
          <li>
            <div class="col value">
              <p class="bold">dailyFee</p>
              <p class="description">
                Number - Amount ($) the staff member is charged per working day
                for cleaning fee
              </p>
              <p class="examples">Examples: <span>5</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Role -->
          <li>
            <div class="col value">
              <p class="bold">role</p>
              <p class="description">
                String - Role of the staff member for permissions
              </p>
              <p class="examples">
                Examples: <span>low, medium, high, admin</span>
              </p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>

          <!-- Services -->
          <li>
            <div class="col value">
              <p class="bold">services</p>
              <p class="description">
                Array - Array of strings (service IDs) that the staff member is
                able to perform
              </p>
              <p class="examples">
                Examples:
                <span
                  >['5f8a1d9b-3b0b-4c9a-9c0d-1a2b3c4d5e6f',
                  '573249b-3b0b-4c9a-9j0d-1a8re74d5e6d']</span
                >
              </p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Custom Commission -->
          <li>
            <div class="col value">
              <p class="bold">customCommissions</p>
              <p class="description">
                Array - Array of objects with categoryId and commission
                properties - Used to override the default commission for a
                service category
              </p>
              <p class="examples">
                Examples:
                <span
                  >[{ categoryId: '5f8a1d9b-3b0b-4c9a-9c0d-1a2b3c4d5e6f',
                  commission: 80}]</span
                >
              </p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Allow Edits -->
          <li>
            <div class="col value">
              <p class="bold">allowsEdits</p>
              <p class="description">
                Boolean - Determines if the salon can edit the Raserva account
                info
              </p>
              <p class="examples">Examples: <span>true</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>

          <!-- Exempt From Service Fees -->
          <li>
            <div class="col value">
              <p class="bold">exemptFromServiceFees</p>
              <p class="description">
                Boolean - Determines if the staff member is exempt from service
                fees
              </p>
              <p class="examples">Examples: <span>false</span></p>
            </div>

            <div class="col">
              <!-- <p class="required">required</p> -->
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="response">
      <h3>Response</h3>

      <!-- Body -->
      <div class="endpoint_body_section path">
        <div class="head">
          <h4>Body</h4>
        </div>

        <ul>
          <li>
            <div class="col value">
              <p class="bold">staff</p>
              <p class="examples">
                Examples:
                <span
                  >{ "staffId": "64ac50907967f3a5c72e8a22", "firstName": "John",
                  "lastName": "Doe", "email": "john@doe.com", "accountCode":
                  "555666", "phoneNumber": "2175551234", "groups": [],
                  "onBooking": false, "onCalendar": false, "commission": 60,
                  "trackHourly": false, "hourlyRate": 0, "splitCheck": 100,
                  "splitOther": 0, "dailyFee": 0, "role": "low", "working":
                  false, "available": false, "schedule": { "default": [ { "day":
                  0, "blocks": [], "_id": "64ac50907967f3a5c72e8a27" }, { "day":
                  1, "blocks": [ { "start": "09:00", "end": "17:00", "_id":
                  "64ac50907967f3a5c72e8a29" } ], "_id":
                  "64ac50907967f3a5c72e8a28" }, { "day": 2, "blocks": [ {
                  "start": "09:00", "end": "17:00", "_id":
                  "64ac50907967f3a5c72e8a2b" } ], "_id":
                  "64ac50907967f3a5c72e8a2a" }, { "day": 3, "blocks": [ {
                  "start": "09:00", "end": "17:00", "_id":
                  "64ac50907967f3a5c72e8a2d" } ], "_id":
                  "64ac50907967f3a5c72e8a2c" }, { "day": 4, "blocks": [ {
                  "start": "09:00", "end": "17:00", "_id":
                  "64ac50907967f3a5c72e8a2f" } ], "_id":
                  "64ac50907967f3a5c72e8a2e" }, { "day": 5, "blocks": [ {
                  "start": "09:00", "end": "17:00", "_id":
                  "64ac50907967f3a5c72e8a31" } ], "_id":
                  "64ac50907967f3a5c72e8a30" }, { "day": 6, "blocks": [], "_id":
                  "64ac50907967f3a5c72e8a32" } ], "exceptions": [], "_id":
                  "64ac50907967f3a5c72e8a26" }, "services": [],
                  "defaultServiceLevel": 1, "additionalTurns": 0, "allowsEdits":
                  false, "isDeleted": false, "exemptFromServiceFees": false,
                  "_id": "64ac50907967f3a5c72e8a25", "customCommissions": [],
                  "customServiceLevels": [], "additionalTurnsLogs": [] }</span
                >
              </p>
            </div>

            <div class="col">
              <p class="required">required</p>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div class="reqResExamples">
      <h3>Examples</h3>

      <!-- Request -->
      <div class="endpoint_body_section path">
        <div class="head">
          <h4>Request</h4>
          <p>application/json</p>
        </div>

        <ul>
          <li>
            <p>
              { "firstName": "John", "lastName": "Doe", "email": "john@doe.com",
              "password": "password", "accountCode": "555666", "phoneNumber":
              "2175551234", "commission": 60, "role": "low" }
            </p>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  computed: {
    apiUrl() {
      return process.env.VUE_APP_RASERVA_BACKEND;
    },
  },
};
</script>

<style lang="scss" scoped>
.endpoint {
  .bold {
    font-weight: 700;
  }
  .tag {
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 700;
    padding: 8px;
    border-radius: 5px;
    color: white;

    &-blue {
      background-color: var(--clr-secondary);
    }
  }

  &_head {
    h2 {
      font-size: 36px;
      margin-bottom: 16px;
    }

    .route {
      padding: 8px 16px;
      border-radius: 5px;
      background-color: var(--clr-white-2);
      display: flex;
      align-items: center;
      gap: 16px;

      .url {
        color: var(--clr-gray);

        span {
          color: var(--clr-primary);
          font-weight: 700;
        }
      }
    }

    .description {
      margin-top: 32px;
    }
  }

  &_body {
    .request,
    .response,
    .reqResExamples {
      margin-top: 32px;
      padding: 16px;
      border-radius: 5px;
      border: 1px solid var(--clr-light);
    }

    h3 {
      font-size: 24px;
      margin-bottom: 32px;
    }

    &_section {
      &:not(:first-child) {
        margin-top: 32px;
      }

      .head {
        display: flex;
        justify-content: space-between;

        h4 {
          font-size: 20px;
          margin-bottom: 16px;
          color: var(--clr-secondary);
        }

        p {
          font-size: 14px;
          color: var(--clr-gray);
        }
      }

      ul {
        .subitem {
          margin-left: 32px;
        }

        li {
          display: flex;
          justify-content: space-between;
          font-size: 14px;

          &:not(:first-child) {
            margin-top: 16px;
          }

          .value {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .bold {
            font-weight: 700;
          }
          .required {
            color: orangered;
          }

          .description {
            margin: 4px 0;
            color: var(--clr-gray);
          }

          .examples {
            span {
              background-color: var(--clr-white-2);
              padding: 4px 8px;
              border-radius: 5px;
              font-size: 12px;
            }
          }
        }
      }
    }
  }
}
</style>
